import { useState, useMemo, useRef } from "react";
import {
  FaSpinner,
  FaTag,
  FaBookmark,
  FaFileAlt,
  FaSearch,
  FaTimes,
  FaUserTag,
} from "react-icons/fa";
import {
  useGetProfilePostsQuery,
  useGetSavedPostsQuery,
} from "../store/api/postApi";
import {
  usePartialEditProfileMutation,
  useDeleteMediaMutation,
} from "../store/api/profileApi";
import { toast } from "react-toastify";
import Post from "./Post";
import styles from "./UserActivity.module.css";

export default function UserActivity({ user, isMyProfile }) {
  const [activeTab, setActiveTab] = useState("about");
  const [cursor, setCursor] = useState(null);

  // Video pitch state (for own profile upload)
  const [videoPending, setVideoPending] = useState(null); // File
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null); // blob URL
  const [isSavingVideo, setIsSavingVideo] = useState(false);
  const videoInputRef = useRef(null);
  const [partialEditProfile] = usePartialEditProfileMutation();
  const [deleteMedia, { isLoading: isDeletingVideo }] =
    useDeleteMediaMutation();
  const [showDeleteVideoConfirm, setShowDeleteVideoConfirm] = useState(false);
  const {
    data: postsData,
    isLoading: postsLoading,
    isFetching: postsFetching,
    isError: postsError,
  } = useGetProfilePostsQuery(
    { userId: user.id, pageSize: 10, cursor },
    { skip: activeTab !== "posts" },
  );

  const {
    data: savedData,
    isLoading: savedLoading,
    isError: savedError,
  } = useGetSavedPostsQuery(undefined, {
    skip: !isMyProfile || activeTab !== "saved",
  });

  const posts = useMemo(() => {
    if (activeTab === "saved") return savedData || [];
    if (activeTab === "posts") {
      return (postsData?.items || []).map((post) =>
        post?.type === "SharedPost"
          ? { ...post, sharedBy: user }
          : { ...post, user },
      );
    }
    return [];
  }, [activeTab, savedData, postsData, user]);

  const nextCursor = postsData?.nextCursor;
  const isLoading = activeTab === "saved" ? savedLoading : postsLoading;
  const isError = activeTab === "saved" ? savedError : postsError;

  // About tab fields — only those NOT already shown in the sidebar
  // (sidebar shows: name, userType, bio, email, phone, location, gender, dob, interests)
  // About tab shows: userType detail, interested fields (expanded), video pitch
  const userTypeLabel =
    user?.userType === "Rookie" && user?.rookieType
      ? `${user.userType} (${user.rookieType})`
      : user?.userType || null;

  // ── Video pitch handlers ──
  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a valid video file");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video must be under 100 MB");
      return;
    }
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPending(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveVideo = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPending(null);
    setVideoPreviewUrl(null);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleSaveVideo = async () => {
    if (!videoPending) return;
    setIsSavingVideo(true);
    try {
      const fd = new FormData();
      fd.append("Video", videoPending);
      await partialEditProfile(fd).unwrap();
      toast.success("Intro video saved!");
      handleRemoveVideo();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save video");
    } finally {
      setIsSavingVideo(false);
    }
  };

  const handleDeleteExistingVideo = async () => {
    try {
      await deleteMedia("video").unwrap();
      toast.success("Intro video removed!");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete video");
    }
  };

  const tabs = [
    { id: "about", label: "About" },
    { id: "posts", label: "Posts", icon: <FaFileAlt /> },
    ...(isMyProfile
      ? [{ id: "saved", label: "Saved", icon: <FaBookmark /> }]
      : []),
  ];

  // About fields that only appear here (not in sidebar)
  const aboutFields = [
    userTypeLabel && {
      icon: <FaUserTag />,
      label: "Role",
      value: userTypeLabel,
    },
    user?.interestedFields && {
      icon: <FaTag />,
      label: "Interested Fields",
      value: user.interestedFields,
    },
    user?.lookingFor && {
      icon: <FaSearch />,
      label: "Looking For",
      value: user.lookingFor,
    },
  ].filter(Boolean);

  return (
    <div className={styles.activity}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.sectionLabel}>Activity</span>
        <div className={styles.tabs} role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── ABOUT TAB ── */}
      {activeTab === "about" && (
        <div className={styles.aboutSection}>
          {/* Bio — full, prominent */}
          <div className={styles.bioCard}>
            <span className={styles.bioCardLabel}>Bio</span>
            {user?.bio ? (
              <p className={styles.bioCardText}>{user.bio}</p>
            ) : (
              <p className={styles.bioPlaceholder}>
                {isMyProfile
                  ? "Add a bio in Edit Profile to tell people about yourself."
                  : "This user has not added a bio yet."}
              </p>
            )}
          </div>

          {/* Role + Interests grid */}
          {aboutFields.length > 0 && (
            <div className={styles.aboutGrid}>
              {aboutFields.map(({ icon, label, value }) => (
                <div className={styles.aboutCard} key={label}>
                  <div className={styles.aboutCardIcon}>{icon}</div>
                  <div className={styles.aboutCardContent}>
                    <span className={styles.aboutLabel}>{label}</span>
                    {label === "Interested Fields" ||
                    label === "Looking For" ? (
                      <div className={styles.tagContainer}>
                        {value.split(",").map((item) => (
                          <span key={item.trim()} className={styles.tag}>
                            {item.trim()}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={styles.aboutValue}>{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Video Pitch ── */}
          <div className={styles.videoPitchSection}>
            <div className={styles.videoPitchTitle}>
              <span>🎬</span> Intro Video
            </div>

            {/* Existing video */}
            {user?.videoPitchUrl && !videoPreviewUrl && (
              <div className={styles.videoPreviewWrap}>
                <video
                  src={user.videoPitchUrl}
                  controls
                  className={styles.videoPlayer}
                />

                {isMyProfile && (
                  <button
                    className={styles.videoRemoveBtn}
                    onClick={() => setShowDeleteVideoConfirm(true)}
                    disabled={isDeletingVideo}
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            {/* Pending video preview */}
            {videoPreviewUrl && (
              <div className={styles.videoPreviewWrap}>
                <video
                  src={videoPreviewUrl}
                  controls
                  className={styles.videoPlayer}
                />
                <button
                  className={styles.videoRemoveBtn}
                  onClick={handleRemoveVideo}
                >
                  <FaTimes />
                </button>
                <button
                  className={styles.videoSaveBtn}
                  onClick={handleSaveVideo}
                  disabled={isSavingVideo}
                >
                  {isSavingVideo ? "Saving…" : "Save Intro Video"}
                </button>
              </div>
            )}

            {/* Upload area — own profile, no video pending */}
            {isMyProfile && !videoPreviewUrl && (
              <>
                <div
                  className={styles.videoUploadArea}
                  onClick={() => videoInputRef.current?.click()}
                >
                  <span className={styles.videoUploadIcon}>🎥</span>
                  <p className={styles.videoUploadText}>
                    {user?.videoPitchUrl
                      ? "Replace intro video"
                      : "Upload an intro video"}
                  </p>
                  <p className={styles.videoUploadHint}>
                    MP4 / MOV · Max 100 MB
                  </p>
                </div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  className={styles.hiddenInput}
                  onChange={handleVideoSelect}
                />
              </>
            )}

            {/* Other user, no video */}
            {!isMyProfile && !user?.videoPitchUrl && (
              <p
                className={styles.bioPlaceholder}
                style={{ textAlign: "center", padding: "16px 0" }}
              >
                No intro video uploaded yet.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── POSTS / SAVED ── */}
      {activeTab !== "about" && (
        <div className={styles.feedSection}>
          {isLoading && (
            <div className={styles.stateContainer}>
              <div className={styles.spinnerWrapper}>
                <FaSpinner className={styles.spinner} />
              </div>
              <p className={styles.stateText}>
                Loading {activeTab === "saved" ? "saved posts" : "posts"}…
              </p>
            </div>
          )}

          {!isLoading && isError && (
            <div className={styles.stateContainer}>
              <div className={styles.errorIcon}>!</div>
              <p className={styles.stateText}>
                Failed to load {activeTab === "saved" ? "saved posts" : "posts"}
                .
              </p>
            </div>
          )}

          {!isLoading && !isError && posts.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                {activeTab === "saved" ? <FaBookmark /> : <FaFileAlt />}
              </div>
              <p className={styles.emptyTitle}>
                {activeTab === "saved" ? "Nothing saved yet" : "No posts yet"}
              </p>
              <p className={styles.emptySubtext}>
                {activeTab === "saved"
                  ? "Bookmark posts to find them here later."
                  : isMyProfile
                    ? "Share your first post with the community."
                    : "This user has not posted anything yet."}
              </p>
            </div>
          )}

          {!isLoading && !isError && posts.length > 0 && (
            <>
              <div className={styles.postsList}>
                {posts.map((post) => (
                  <Post
                    key={post.id}
                    post={post}
                    isOwner={activeTab === "posts" && isMyProfile}
                  />
                ))}
              </div>

              {activeTab === "posts" && nextCursor && (
                <div className={styles.loadMoreContainer}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={() => setCursor(nextCursor)}
                    disabled={postsFetching}
                  >
                    {postsFetching ? (
                      <>
                        <FaSpinner className={styles.btnSpinner} /> Loading…
                      </>
                    ) : (
                      "Load more posts"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showDeleteVideoConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmModal}>
            <div className={styles.confirmIcon}>🎥</div>

            <h3 className={styles.confirmTitle}>Delete Intro Video?</h3>

            <p className={styles.confirmText}>
              This video will be permanently removed.
            </p>

            <div className={styles.confirmActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowDeleteVideoConfirm(false)}
              >
                Cancel
              </button>

              <button
                className={styles.deleteBtn}
                onClick={async () => {
                  await handleDeleteExistingVideo();
                  setShowDeleteVideoConfirm(false);
                }}
                disabled={isDeletingVideo}
              >
                {isDeletingVideo ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
